<template>
  <transition name="fade">
    <div v-if="isOpen" class="mp-overlay" @keydown.esc.prevent.stop="close">
      <!-- Backdrop -->
      <div class="mp-backdrop" @click="close"></div>

      <!-- Modal -->
      <div class="mp-shell" role="dialog" aria-modal="true" :aria-labelledby="dialogTitleId">
        <!-- Header -->
        <div class="mp-header">
          <h2 :id="dialogTitleId" class="mp-title">{{ tituloHeader }}</h2>
          <button ref="closeBtn" class="mp-iconbtn" @click="close" aria-label="Cerrar">
            <i class="material-icons">close</i>
          </button>
        </div>

        <!-- Body -->
        <div class="mp-body">
          <!-- Loading -->
          <div v-if="loading" class="mp-skeleton">
            <div class="mp-skel-line"></div>
            <div class="mp-skel-box"></div>
            <div class="mp-skel-box tall"></div>
          </div>

          <!-- Error -->
          <div v-else-if="error" class="mp-error">
            <div class="mp-error-title">
              <i class="material-icons">error_outline</i>
              <span>Error cargando el perfil</span>
            </div>
            <p class="mp-error-text">{{ error }}</p>
            <button class="mp-btn mp-btn-danger" @click="retry">
              <i class="material-icons">refresh</i>
              <span>Reintentar</span>
            </button>
          </div>

          <!-- Content -->
          <div v-else class="mp-content">
            <!-- Perfil principal -->
            <section class="mp-card">
              <div class="mp-row mp-row-between mp-gap">
                <div class="mp-row mp-gap">
                  <div
                    class="mp-avatar"
                    :style="{ backgroundImage: 'url(' + (profesional_.foto ? profesional_.foto : require('@/assets/empty-user.png')) + ')' }"
                  ></div>

                  <div class="mp-col">
                    <div class="mp-row mp-gap-small mp-align-center">
                      <h3 class="mp-h3">{{ profesional_.nombre }} {{ profesional_.apellido }}</h3>
                      <i class="material-icons mp-verified" title="Identidad verificada">verified</i>
                    </div>

                    <div v-if="profesional_.clasificacion_n > 0" class="mp-row mp-gap-small mp-align-center">
                      <i class="material-icons mp-star">star</i>
                      <span class="mp-strong">
                        {{ (profesional_.clasificacion_suma/profesional_.clasificacion_n).toFixed(1) }}
                      </span>
                      <span class="mp-muted">({{ profesional_.clasificacion_n }} opiniones)</span>
                    </div>
                    <div v-else class="mp-muted">(Sin Clasificación)</div>

                    <div class="mp-row mp-gap-small mp-wrap mp-badges">
                      <span v-if="profesional_.puntaje_busqueda > 70" class="mp-chip mp-chip-warn">
                        Top <i class="material-icons mp-chip-icon">emoji_events</i>
                      </span>
                      <span v-if="calcularTiempoActivo(profesional_) < 1440" class="mp-chip mp-chip-info">
                        <span v-if="calcularTiempoActivo(profesional_) < 60">Activo hace minutos</span>
                        <span v-else-if="calcularTiempoActivo(profesional_) < 120">Activo hace 1 hora</span>
                        <span v-else>Activo en las últimas horas</span>
                        <i class="material-icons mp-chip-icon">hourglass_top</i>
                      </span>
                      <span v-if="profesional_.response_time_score > 50" class="mp-chip mp-chip-ok">
                        Respuesta rápida <i class="material-icons mp-chip-icon">schedule</i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <p class="mp-text" v-html="profesional_.descripcion"></p>

              <div class="mp-row mp-wrap mp-gap-small">
                <span v-for="c in categorias_" :key="c" class="mp-chip mp-chip-info">
                  {{ categoriaNombre(c) }}
                </span>
              </div>
            </section>

            <!-- Certificaciones y Seguros -->
            <section class="mp-grid">
              <div class="mp-card">
                <div class="mp-row mp-gap mp-align-center">
                  <i class="material-icons mp-icon-lg mp-primary">verified_user</i>
                  <h4 class="mp-h4">Certificaciones y credenciales</h4>
                </div>
                <div class="mp-col mp-gap-small">
                  <div
                    v-for="cert in (profesional_.certificaciones || [])"
                    :key="cert.id"
                    class="mp-listitem"
                  >
                    <i class="material-icons mp-primary">{{ iconoCert(cert.categoria) }}</i>
                    <div class="mp-listtext">
                      <p class="mp-strong mp-tight">{{ cert.nombre }}</p>
                      <p class="mp-small mp-tight mp-muted">{{ cert.detalle }}</p>
                    </div>
                  </div>
                  <p v-if="!profesional_.certificaciones || profesional_.certificaciones.length === 0" class="mp-muted">
                    Sin certificaciones registradas.
                  </p>
                </div>
              </div>

              <div class="mp-card">
                <div class="mp-row mp-gap mp-align-center">
                  <i class="material-icons mp-icon-lg mp-primary">shield</i>
                  <h4 class="mp-h4">Seguro y garantías</h4>
                </div>
                <div class="mp-col mp-gap-small">
                  <div class="mp-listitem">
                    <i class="material-icons mp-ok">attach_money</i>
                    <div class="mp-listtext">
                      <p class="mp-strong mp-tight">Garantía Jobbing</p>
                      <p class="mp-small mp-tight mp-muted">Se cubre hasta el monto pagado en la app</p>
                    </div>
                  </div>

                  <div
                    v-for="seg in (profesional_.seguros || [])"
                    :key="seg.id"
                    class="mp-listitem"
                  >
                    <i class="material-icons mp-primary">verified</i>
                    <div class="mp-listtext">
                      <p class="mp-strong mp-tight">{{ seg.nombre }}</p>
                      <p class="mp-small mp-tight mp-muted">{{ seg.detalle }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <!-- Galería -->
            <section v-if="galleryItems.length > 0" class="mp-section">
              <h4 class="mp-h4">Trabajos realizados</h4>
              <div class="mp-gallery">
                <div
                  v-for="g in galleryItems.slice(0, max_g)"
                  :key="(g.type === 'project' ? 'p-' : 'f-') + g.item.id"
                  class="mp-thumb"
                  :style="{ backgroundImage: 'url(' + (g.type === 'project' ? (g.item.fotos[0] ? g.item.fotos[0].link : '') : g.item.link) + ')' }"
                  @click="g.type === 'project'
                          ? openLightbox((g.item.fotos || []).map(f=>f.link), 0)
                          : openLightbox(galeria.map(f=>f.link), galeria.map(f=>f.link).indexOf(g.item.link))"
                >
                  <div v-if="g.type === 'project'" class="mp-thumb-label">
                    {{ g.item.nombre }} ({{ g.item.fotos.length }} fotos)
                  </div>
                </div>
              </div>

              <div v-if="max_g < galleryItems.length" class="mp-center">
                <button class="mp-link" @click="max_g += 4">Ver más</button>
              </div>
            </section>

            <!-- Opiniones -->
            <section v-if="calificaciones.length > 0" class="mp-card">
              <h4 class="mp-h4">Opiniones de clientes</h4>
              <div class="mp-col mp-gap">
                <div v-for="c in calificaciones" :key="c.id" class="mp-review">
                  <img
                    :src="c.usuario.img ? c.usuario.img : require('@/assets/empty-user.png')"
                    alt="Avatar"
                    class="mp-avatar sm"
                  />
                  <div class="mp-col">
                    <div class="mp-row mp-gap-small mp-align-center">
                      <h5 class="mp-strong mp-tight">{{ c.usuario.nombre }}</h5>
                      <span class="mp-small mp-muted">
                        {{ new Date(c.fecha_creado.a, c.fecha_creado.m-1, c.fecha_creado.d).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' }) }}
                      </span>
                    </div>
                    <div class="mp-row">
                      <i v-for="i in c.calificacion" :key="i" class="material-icons mp-star">star</i>
                      <i v-for="i in 5 - c.calificacion" :key="'e'+i" class="material-icons mp-star-empty">star_border</i>
                    </div>
                    <p class="mp-text sm">
                      {{ c.comentario || (c.usuario.nombre + ' no dejó un comentario.') }}
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        <!-- Footer -->
        <div class="mp-footer">
          <span class="mp-small mp-muted">Pulsa ESC para cerrar</span>
        </div>

        <!-- Lightbox -->
        <transition name="fade">
          <div v-if="lightbox.open" class="lb-overlay" @keydown.esc.prevent.stop="closeLightbox">
            <div class="lb-backdrop" @click="closeLightbox"></div>
            <div class="lb-content">
              <img
                :src="currentLightboxSrc"
                class="lb-image"
                @load="imgLoaded = true"
                @dblclick="toggleZoom"
                :style="zoomStyle"
                alt="Imagen"
                draggable="false"
              />

              <button class="lb-iconbtn lb-close" @click="closeLightbox" aria-label="Cerrar imagen">
                <i class="material-icons">close</i>
              </button>

              <button v-if="lightbox.images.length > 1" class="lb-iconbtn lb-prev" @click.stop="prevImg" aria-label="Anterior">
                <i class="material-icons">chevron_left</i>
              </button>

              <button v-if="lightbox.images.length > 1" class="lb-iconbtn lb-next" @click.stop="nextImg" aria-label="Siguiente">
                <i class="material-icons">chevron_right</i>
              </button>

              <div v-if="lightbox.images.length > 1" class="lb-counter">
                {{ lightbox.index + 1 }} / {{ lightbox.images.length }}
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from 'axios';
import {mapGetters} from 'vuex';
export default {
  name: 'ModalPerfilProfesional',
  props: {
    categorias: { type: Array, default: () => [] },
    endpoint: {
      type: String,
      default: () => (window?.hostname || '') + 'api/get_perfil_profesional_nombre_seo',
    },
    payloadKey: { type: String, default: 'nombre_seo' },
    axiosConfig: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      isOpen: false,
      loading: false,
      error: null,
      profesional_: {},
      calificaciones: [],
      proyectos: [],
      galeria: [],
      categorias_: [],
      max_g: 6,
      lastNombreSeo: null,
      cancelSource: null,
      dialogTitleId: 'dlg-' + Math.random().toString(36).slice(2),

      // Lightbox
      lightbox: { open: false, images: [], index: 0, zoom: 1 },
      imgLoaded: false,
    };
  },
  computed: {
    ...mapGetters(['categorias']),
    tituloHeader() {
      if (this.loading) return 'Cargando perfil…';
      if (this.error) return 'No se pudo cargar el perfil';
      const p = this.profesional_;
      return p?.nombre ? `${p.nombre} ${p.apellido}` : 'Perfil del profesional';
    },
    galleryItems() {
      const projectPhotoIds = new Set();
      const projectItems = (this.proyectos || []).map(p => {
        (p.fotos || []).forEach(f => projectPhotoIds.add(f.id));
        return { type: 'project', item: p };
      });
      const photoItems = (this.galeria || [])
        .filter(f => !projectPhotoIds.has(f.id))
        .map(f => ({ type: 'photo', item: f }));
      return projectItems.concat(photoItems);
    },
    currentLightboxSrc() {
      return this.lightbox.images[this.lightbox.index] || '';
    },
    zoomStyle() {
      return this.lightbox.zoom !== 1
        ? { transform: `scale(${this.lightbox.zoom})`, transformOrigin: 'center center' }
        : {};
    },
  },
  methods: {
    async openByNombreSeo(nombreSeo) {
      this.isOpen = true;
      document.documentElement.classList.add('mp-noscroll');
      this.$nextTick(() => this.$refs.closeBtn?.focus());

      this.lastNombreSeo = nombreSeo;
      await this.fetchData(nombreSeo);

      window.addEventListener('keydown', this.onKeydown);
    },
    async retry() {
      if (this.lastNombreSeo) await this.fetchData(this.lastNombreSeo);
    },
    async fetchData(nombreSeo) {
      if (this.cancelSource) this.cancelSource.cancel('Nueva carga iniciada');
      this.cancelSource = axios.CancelToken ? axios.CancelToken.source() : null;

      this.loading = true;
      this.error = null;

      try {
        const body = { [this.payloadKey]: nombreSeo };
        const res = await axios.post(this.endpoint, body, {
          ...(this.cancelSource ? { cancelToken: this.cancelSource.token } : {}),
          ...this.axiosConfig,
        });

        const r = res.data || {};
        this.profesional_ = r.profesional || {};
        this.calificaciones = (r.calificaciones || []).sort(
          (a, b) =>
            new Date(b.fecha_creado.a, b.fecha_creado.m - 1, b.fecha_creado.d) -
            new Date(a.fecha_creado.a, a.fecha_creado.m - 1, a.fecha_creado.d)
        );
        this.proyectos = r.proyectos || [];
        this.galeria = r.galeria || [];
        this.categorias_ = r.categorias || [];

        let suma_ = 0;
        for (const c of this.calificaciones) suma_ += c.calificacion || 0;
        if (typeof this.profesional_.clasificacion_suma !== 'number') this.profesional_.clasificacion_suma = suma_;
        if (typeof this.profesional_.clasificacion_n !== 'number') this.profesional_.clasificacion_n = this.calificaciones.length;
      } catch (e) {
        if (!axios.isCancel(e)) this.error = e?.response?.data?.message || e?.message || 'Error desconocido';
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.isOpen = false;
      document.documentElement.classList.remove('mp-noscroll');
      this.max_g = 6;
      if (this.cancelSource) this.cancelSource.cancel('Modal cerrado');
      this.teardownKeyListener();
      this.closeLightbox();
    },
    categoriaNombre(id) {
      const c = (this.categorias || []).find(z => z.id == id);
      return c ? c.nombre : id;
    },
    iconoCert(categoria) {
      const mapa = { 'Certificado de antecedentes': 'gavel', 'Carnet de identidad': 'badge' };
      return mapa[categoria] || 'school';
    },
    calcularTiempoActivo(user) {
      if (!user || !user.ultima_actividad) return Infinity;
      const ultimaActividad = new Date(user.ultima_actividad);
      const ahora = new Date();
      return Math.floor((ahora - ultimaActividad) / 60000);
    },

    // Lightbox
    openLightbox(urls = [], index = 0) {
      const images = (urls || []).filter(Boolean);
      if (!images.length) return;
      this.lightbox.images = images;
      this.lightbox.index = Math.max(0, Math.min(index, images.length - 1));
      this.lightbox.zoom = 1;
      this.lightbox.open = true;
      this.imgLoaded = false;
      // keep scroll locked
      document.documentElement.classList.add('mp-noscroll');
    },
    closeLightbox() {
      this.lightbox.open = false;
      this.lightbox.images = [];
      this.lightbox.index = 0;
      this.lightbox.zoom = 1;
      this.imgLoaded = false;
      if (!this.isOpen) document.documentElement.classList.remove('mp-noscroll');
    },
    nextImg() {
      if (!this.lightbox.open) return;
      this.lightbox.index = (this.lightbox.index + 1) % this.lightbox.images.length;
      this.imgLoaded = false;
      this.lightbox.zoom = 1;
    },
    prevImg() {
      if (!this.lightbox.open) return;
      this.lightbox.index = (this.lightbox.index - 1 + this.lightbox.images.length) % this.lightbox.images.length;
      this.imgLoaded = false;
      this.lightbox.zoom = 1;
    },
    toggleZoom() {
      this.lightbox.zoom = this.lightbox.zoom === 1 ? 1.6 : 1;
    },
    onKeydown(e) {
      if (!this.lightbox.open) return;
      if (e.key === 'ArrowRight') this.nextImg();
      else if (e.key === 'ArrowLeft') this.prevImg();
      else if (e.key === 'Escape') this.closeLightbox();
    },
    teardownKeyListener() {
      window.removeEventListener('keydown', this.onKeydown);
    },
  },
  beforeDestroy() {
    document.documentElement.classList.remove('mp-noscroll');
    if (this.cancelSource) this.cancelSource.cancel('Destroy');
    this.teardownKeyListener();
  },
};
</script>

<style scoped>
/* ---------- Theme ---------- */
:host, .mp-shell {
  --mp-primary: #2563eb; /* accent */
  --mp-ok: #16a34a;
  --mp-warn: #ca8a04;
  --mp-danger: #b91c1c;

  --mp-bg: #ffffff;
  --mp-bg-soft: #f6f7f9;
  --mp-text: #0f172a;
  --mp-muted: #64748b;
  --mp-border: #e5e7eb;

  --mp-radius-lg: 16px;
  --mp-radius-md: 12px;
  --mp-radius-sm: 10px;
  --mp-shadow: 0 10px 30px rgba(0,0,0,.12);
}

/* ---------- Base layout ---------- */
.mp-overlay {
  position: fixed; inset: 0; z-index: 1000;
  display: grid; place-items: center;
}
.mp-backdrop {
  position: absolute; inset: 0; background: rgba(0,0,0,.5);
}
.mp-shell {
  position: relative; width: min(100%, 1040px); max-height: 90vh;
  background: var(--mp-bg); border-radius: var(--mp-radius-lg); box-shadow: var(--mp-shadow);
  display: grid; grid-template-rows: auto 1fr auto; overflow: hidden;
  border: 1px solid var(--mp-border);
}
.mp-header, .mp-footer {
  display: flex; align-items: center; justify-content: space-between;
  background: rgba(255,255,255,.9); backdrop-filter: blur(8px);
  padding: 14px 20px; border-bottom: 1px solid var(--mp-border);
}
.mp-footer { border-top: 1px solid var(--mp-border); border-bottom: none; justify-content: flex-end; }
.mp-title { margin: 0; font-size: 18px; color: var(--mp-text); font-weight: 700; }
.mp-body { overflow: auto; padding: 20px; background: var(--mp-bg-soft); }
.mp-content { display: grid; gap: 20px; }

/* ---------- Cards & layout ---------- */
.mp-card {
  background: var(--mp-bg);
  border: 1px solid var(--mp-border);
  border-radius: var(--mp-radius-lg);
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,.03);
}
.mp-grid {
  display: grid; gap: 20px;
  grid-template-columns: 1fr;
}
@media (min-width: 900px) {
  .mp-grid { grid-template-columns: 1fr 1fr; }
}

/* ---------- Typography ---------- */
.mp-h3 { margin: 0; font-size: 20px; font-weight: 800; color: var(--mp-text); }
.mp-h4 { margin: 0 0 8px; font-size: 18px; font-weight: 800; color: var(--mp-text); }
.mp-strong { font-weight: 700; color: var(--mp-text); }
.mp-text { color: #334155; line-height: 1.6; margin: 10px 0 0; }
.mp-text.sm { font-size: 14px; }
.mp-small { font-size: 12px; }
.mp-muted { color: var(--mp-muted); }
.mp-tight { margin: 0; padding: 0; }

/* ---------- Avatars ---------- */
.mp-avatar {
  width: 96px; height: 96px; border-radius: 999px;
  background-size: cover; background-position: center; box-shadow: 0 4px 14px rgba(0,0,0,.12);
}
.mp-avatar.sm { width: 48px; height: 48px; border-radius: 999px; }

/* ---------- Rows/Cols helpers ---------- */
.mp-row { display: flex; }
.mp-row-between { justify-content: space-between; }
.mp-col { display: flex; flex-direction: column; }
.mp-align-center { align-items: center; }
.mp-gap { gap: 16px; }
.mp-gap-small { gap: 8px; }
.mp-wrap { flex-wrap: wrap; }
.mp-center { text-align: center; }

/* ---------- Chips / badges ---------- */
.mp-badges { margin-top: 4px; }
.mp-chip {
  display: inline-flex; align-items: center; gap: 6px;
  border-radius: 999px; padding: 4px 10px; font-weight: 700; font-size: 12px;
  border: 1px solid transparent;
}
.mp-chip .mp-chip-icon { font-size: 16px; line-height: 1; }
.mp-chip-info { background: #e0ecff; color: #1e40af; }
.mp-chip-ok { background: #e8f7ed; color: #166534; }
.mp-chip-warn { background: #fff2cc; color: #92400e; }

/* ---------- Icons ---------- */
.mp-iconbtn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 36px; height: 36px; border-radius: 999px; border: none; background: #f3f4f6; cursor: pointer;
}
.mp-iconbtn:hover { background: #e5e7eb; }
.mp-icon-lg { font-size: 30px; }
.mp-primary { color: var(--mp-primary); }
.mp-ok { color: var(--mp-ok); }
.mp-verified { color: var(--mp-primary); }
.mp-star { color: #f59e0b; font-size: 18px; }
.mp-star-empty { color: #d1d5db; font-size: 18px; }

/* ---------- List items ---------- */
.mp-listitem {
  display: grid; grid-template-columns: 24px 1fr; gap: 10px;
  align-items: start; background: #f9fafb; border-radius: var(--mp-radius-md);
  padding: 10px 12px; border: 1px solid #eef2f7;
}
.mp-listitem:hover { background: #f3f4f6; }
.mp-listtext p { margin: 0; }

/* ---------- Gallery ---------- */
.mp-section { display: grid; gap: 10px; }
.mp-gallery {
  display: grid; gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}
.mp-thumb {
  position: relative; aspect-ratio: 16/9; background-size: cover; background-position: center;
  border-radius: var(--mp-radius-md); box-shadow: 0 6px 20px rgba(0,0,0,.08);
  transition: transform .2s ease; cursor: pointer;
}
.mp-thumb:hover { transform: scale(1.02); }
.mp-thumb-label {
  position: absolute; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,.6); color: #fff; font-size: 12px; font-weight: 700;
  padding: 6px 8px; border-bottom-left-radius: var(--mp-radius-md); border-bottom-right-radius: var(--mp-radius-md);
}

/* ---------- Reviews ---------- */
.mp-review { display: grid; grid-template-columns: 48px 1fr; gap: 12px; }

/* ---------- Buttons / Links ---------- */
.mp-btn {
  display: inline-flex; align-items: center; gap: 8px;
  border: none; border-radius: 10px; padding: 10px 14px; font-weight: 700; cursor: pointer;
  background: var(--mp-primary); color: #fff; box-shadow: 0 4px 12px rgba(0,0,0,.12);
}
.mp-btn:hover { filter: brightness(0.95); }
.mp-btn-danger { background: var(--mp-danger); }
.mp-link {
  background: none; border: none; color: var(--mp-primary); font-weight: 700; cursor: pointer;
  padding: 6px 8px; border-radius: 8px;
}
.mp-link:hover { text-decoration: underline; }

/* ---------- Error box ---------- */
.mp-error {
  display: grid; gap: 10px; background: #fef2f2; border: 1px solid #fecaca;
  color: #7f1d1d; border-radius: var(--mp-radius-md); padding: 14px;
}
.mp-error-title { display: inline-flex; align-items: center; gap: 8px; font-weight: 800; }
.mp-error-text { margin: 0 0 4px 0; }

/* ---------- Skeleton ---------- */
@keyframes mp-shimmer {
  0% { background-position: -200px 0; }
  100% { background-position: 200px 0; }
}
.mp-skeleton .mp-skel-line {
  height: 24px; width: 180px; border-radius: 8px;
  background: linear-gradient(90deg,#eee 25%,#f5f5f5 37%,#eee 63%);
  background-size: 400px 100%; animation: mp-shimmer 1.2s infinite;
}
.mp-skeleton .mp-skel-box {
  height: 96px; border-radius: var(--mp-radius-lg); background: #f3f4f6;
  animation: mp-shimmer 1.2s infinite; background-image: linear-gradient(90deg,#eee 25%,#f5f5f5 37%,#eee 63%); background-size: 400px 100%;
  margin-top: 10px;
}
.mp-skeleton .mp-skel-box.tall { height: 210px; }

/* ---------- Lightbox ---------- */
.lb-overlay {
  position: fixed; inset: 0; z-index: 1100; display: grid; place-items: center;
}
.lb-backdrop { position: absolute; inset: 0; background: rgba(0,0,0,.82); }
.lb-content { position: relative; max-width: 92vw; max-height: 90vh; padding: 10px; }
.lb-image {
  max-width: 92vw; max-height: 80vh; border-radius: 14px; box-shadow: var(--mp-shadow);
  object-fit: contain; user-select: none; transition: transform .15s ease;
}
.lb-iconbtn {
  position: absolute; display: inline-flex; align-items: center; justify-content: center;
  width: 40px; height: 40px; border-radius: 999px; border: none; background: rgba(255,255,255,.9);
  cursor: pointer; box-shadow: 0 4px 12px rgba(0,0,0,.24);
}
.lb-iconbtn:hover { background: #fff; }
.lb-close { top: 8px; right: 8px; }
.lb-prev { top: 50%; left: 8px; transform: translateY(-50%); }
.lb-next { top: 50%; right: 8px; transform: translateY(-50%); }
.lb-counter {
  position: absolute; bottom: 8px; left: 50%; transform: translateX(-50%);
  color: #fff; font-size: 12px; background: rgba(0,0,0,.4); padding: 4px 8px; border-radius: 999px;
}

/* ---------- Transitions ---------- */
.fade-enter-active, .fade-leave-active { transition: opacity .15s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }

/* ---------- Prevent body scroll when open ---------- */
:global(html).mp-noscroll { overflow: hidden; }
</style>
